declare global {
  interface Window {
    _hydrationResetTimeout?: number;
  }
}

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useParams, useLocation } from "wouter"
import { ArrowRight, ArrowLeft, Users, ChevronDown, ChevronRight, AlertTriangle, CheckCircle, Plus, X, Award, Calendar, HandHeart, Info } from "lucide-react"
import { useValueAssessment, SaveSummarySectionOptions } from "@/contexts/ValueAssessmentContext"
import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { R2R_ACTIVITIES, getTaskGroups, getActivitiesByGroup, REGIONS } from "@/data/r2rActivities"
import { apiRequest } from "@/lib/queryClient"
import { useUnsavedChangesWarning } from "@/hooks/useUnsavedChangesWarning"
import { getActivityHelpText } from "@shared/constants/activityHelpTextV2"
import { DEFAULT_RAMP_VECTOR, normalizeRampByPillar, type RampVector } from "@shared/utils/rampByPillar"

interface FTEAllocationData {
  regions: Array<{
    regionName: string
    totalFTEs: number
    activityBreakdown: Record<string, number>
  }>
  validationStatus: 'valid' | 'under-allocated' | 'invalid'
  allocationComplete: boolean
  overallAllocation: string
}

export default function TaskFirstFTEAllocationScreen() {
  const params = useParams()
  const [location, navigate] = useLocation()

  const projectId = params.projectId
  const { data, updateSection, saveSummarySection } = useValueAssessment()
  // Public token detection for read/write without auth
  const qs = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
  const publicToken = (qs?.get('t') || (typeof window !== 'undefined' ? sessionStorage.getItem('public_token') || '' : '')) as string
  const isPublic = Boolean((qs && qs.get('public') === 'true') || publicToken)

  // FTE allocation matrix: [activity][region] = FTE count
  const [fteMatrix, setFTEMatrix] = useState<Record<string, Record<string, number>>>({})
  // FIXED: Add string state for input values during typing to prevent auto-save interference
  const [inputValues, setInputValues] = useState<Record<string, Record<string, string>>>({})
  const [customActivities, setCustomActivities] = useState<Array<{id: string, name: string, frequency: 'Daily' | 'Monthly', group: string}>>([])
  const [removedActivityIds, setRemovedActivityIds] = useState<string[]>([])
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [regionalTotals, setRegionalTotals] = useState<Record<string, number>>({})
  const [totalFTEsPerRegion, setTotalFTEsPerRegion] = useState<Record<string, number>>({})
  const [fteCostsByRegion, setFteCostsByRegion] = useState<Record<string, number>>({
    'North America': 75000,
    'EMEA': 60000,
    'APAC': 45000,
    'India': 25000,
    'Shared Services': 40000
  })

  // Track if a user is actively editing to prevent hydration overwrites
  const isUserEditingRef = useRef(false)
  const hasHydratedRef = useRef(false)
  const isHydratingRef = useRef(false)  // Track if hydration is currently in progress
  const [hasInitialized, setHasInitialized] = useState(false)
  const [hasLocalChanges, setHasLocalChanges] = useState(false)

  const averageFTECost = useMemo(() => {
    const costs = Object.values(fteCostsByRegion || {})
    const activeCosts = costs.filter(cost => cost > 0)
    if (activeCosts.length === 0) return 0
    const total = activeCosts.reduce((sum, cost) => sum + cost, 0)
    return Math.round(total / activeCosts.length)
  }, [fteCostsByRegion])

  // Automation percentages for each activity - NEW FUNCTIONALITY
  const [automationPercentages, setAutomationPercentages] = useState<Record<string, number>>({})

  // State for collapsible FTE costs section
  const [showFTECosts, setShowFTECosts] = useState(false)

  // State for collapsible sections
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    other: false
  })

  // Public client completion state
  const [showCompletionMessage, setShowCompletionMessage] = useState(false)
  const [selectedActivityForHelp, setSelectedActivityForHelp] = useState<string | null>(null)
  const [rationaleOverrides, setRationaleOverrides] = useState<Record<string, string>>({})
  const [rationaleDraft, setRationaleDraft] = useState("")
  const [rampByPillarYear, setRampByPillarYear] = useState<Record<string, RampVector>>({})
  const [selectedPillarForRampModal, setSelectedPillarForRampModal] = useState<string | null>(null)

  // ─────────────── FTE Allocation Toggle: Start ───────────────
  // Global allocation mode toggle (persisted)
  const [allocationMode, setAllocationMode] = useState<'activity' | 'pillar'>('activity')
  // Hide legacy per-group toggle
  const showPerGroupToggle = false
  // Pillar-level allocation state
  const [pillarFTEAllocation, setPillarFTEAllocation] = useState<Record<string, { ftesByRegion: Record<string, number>, automationPercentage?: number }>>({})
  // Per-group view mode (Activity vs Pillar)
  const [viewMode, setViewMode] = useState<Record<string, 'activity' | 'pillar'>>({})
  // ─────────────── FTE Allocation Toggle: End ───────────────

  // Hydrate global allocation mode from persisted data
  useEffect(() => {
    const persisted = (data?.taskFTEAllocation as any)?.allocationMode || (data?.summary_data as any)?.taskFTEAllocation?.allocationMode;
    if (persisted && (persisted === 'activity' || persisted === 'pillar')) {
      setAllocationMode(persisted);
    }
  }, [data?.taskFTEAllocation?.allocationMode, data?.summary_data?.taskFTEAllocation?.allocationMode]);

  // Hydrate pillarFTEAllocation and initialize per-group view modes
  useEffect(() => {
    const persistedPillar = (data?.taskFTEAllocation as any)?.pillarFTEAllocation || (data?.summary_data as any)?.taskFTEAllocation?.pillarFTEAllocation;
    if (persistedPillar && typeof persistedPillar === 'object') {
      setPillarFTEAllocation(persistedPillar);
    }

    // Build desired view mode from persisted pillar data (only treat as pillar if any non-zero values)
    const desired: Record<string, 'activity' | 'pillar'> = {};
    const groups = getTaskGroups();
    const groupHasNonZeroPillar = (g: string) => {
      const grp = persistedPillar?.[g];
      if (!grp || !grp.ftesByRegion) return false;
      return REGIONS.some(r => (grp.ftesByRegion[r] || 0) > 0);
    };
    const hasAnyPillar = !!persistedPillar && groups.some(g => groupHasNonZeroPillar(g));

    groups.forEach(group => {
      // If this group has any non-zero pillar data, default to pillar; otherwise activity
      desired[group] = groupHasNonZeroPillar(group) ? 'pillar' : 'activity';
    });

    // If no pillar groups are enabled at all, ensure all default to activity
    if (!hasAnyPillar) {
      groups.forEach(g => desired[g] = 'activity');
    }

    // Only update viewMode if different, and not while user is actively editing
    if (!isUserEditingRef.current) {
      const different = Object.keys(desired).some(k => desired[k] !== viewMode[k]);
      if (different || Object.keys(viewMode).length === 0) {
        setViewMode(desired);
      }
    }
  }, [data?.taskFTEAllocation?.pillarFTEAllocation, data?.summary_data?.taskFTEAllocation?.pillarFTEAllocation, allocationMode]);

  useEffect(() => {
    const persistedOverrides =
      (data?.taskFTEAllocation as any)?.rationaleOverrides ||
      (data?.summary_data as any)?.taskFTEAllocation?.rationaleOverrides ||
      (data?.summary_data as any)?.valueSummary?.rationaleOverrides ||
      {};

    setRationaleOverrides(
      (persistedOverrides && typeof persistedOverrides === "object" ? persistedOverrides : {}) as Record<string, string>
    );
  }, [
    data?.taskFTEAllocation?.rationaleOverrides,
    data?.summary_data?.taskFTEAllocation?.rationaleOverrides,
    data?.summary_data?.valueSummary?.rationaleOverrides
  ]);

  useEffect(() => {
    const persistedRamp =
      (data?.taskFTEAllocation as any)?.rampByPillarYear ||
      (data?.summary_data as any)?.taskFTEAllocation?.rampByPillarYear ||
      (data?.summary_data as any)?.valueSummary?.rampByPillarYear ||
      {};

    const groups = getTaskGroups();
    setRampByPillarYear(normalizeRampByPillar(persistedRamp, groups));
  }, [
    data?.taskFTEAllocation?.rampByPillarYear,
    data?.summary_data?.taskFTEAllocation?.rampByPillarYear,
    data?.summary_data?.valueSummary?.rampByPillarYear
  ]);

  const preventHydrationTemporarily = () => {
    isUserEditingRef.current = true;
    // Clear any existing timer to avoid overlapping resets
    clearTimeout(window._hydrationResetTimeout);
    // Set a new delay before hydration can resume
    window._hydrationResetTimeout = window.setTimeout(() => {
      isUserEditingRef.current = false;
    }, 1000); // 🔁 adjust this as needed
  };

  const markUnsaved = useCallback(() => {
    setHasLocalChanges(true);
  }, []);

  const getEffectiveRationale = useCallback((activityName: string) => {
    return rationaleOverrides[activityName] || getActivityHelpText(activityName);
  }, [rationaleOverrides]);

  const openRationaleModal = useCallback((activityName: string) => {
    setSelectedActivityForHelp(activityName);
    setRationaleDraft(getEffectiveRationale(activityName));
  }, [getEffectiveRationale]);

  const persistRationaleOverrides = useCallback((nextOverrides: Record<string, string>) => {
    safeUpdateSection('taskFTEAllocation', {
      pillarFTEAllocation,
      rampByPillarYear,
      rationaleOverrides: nextOverrides,
      allocationMode,
      totalFTEsPerRegion: { ...totalFTEsPerRegion },
      fteCostsByRegion: { ...fteCostsByRegion },
      regionalTotals: { ...regionalTotals },
      fteMatrix: { ...fteMatrix },
      customActivities: [...customActivities],
      automationPercentages: { ...automationPercentages }
    });
  }, [
    allocationMode,
    automationPercentages,
    customActivities,
    fteCostsByRegion,
    fteMatrix,
    pillarFTEAllocation,
    rampByPillarYear,
    regionalTotals,
    totalFTEsPerRegion
  ]);

  const saveRationaleOverride = useCallback(() => {
    if (!selectedActivityForHelp) return;
    const next = {
      ...rationaleOverrides,
      [selectedActivityForHelp]: rationaleDraft.trim() || getActivityHelpText(selectedActivityForHelp)
    };
    setRationaleOverrides(next);
    persistRationaleOverrides(next);
    markUnsaved();
    setSelectedActivityForHelp(null);
  }, [selectedActivityForHelp, rationaleOverrides, rationaleDraft, persistRationaleOverrides, markUnsaved]);

  const resetRationaleToDefault = useCallback(() => {
    if (!selectedActivityForHelp) return;
    const next = { ...rationaleOverrides };
    delete next[selectedActivityForHelp];
    setRationaleOverrides(next);
    setRationaleDraft(getActivityHelpText(selectedActivityForHelp));
    persistRationaleOverrides(next);
    markUnsaved();
  }, [selectedActivityForHelp, rationaleOverrides, persistRationaleOverrides, markUnsaved]);

  // Initialize matrix from existing data - FIXED HYDRATION LOGIC
  useEffect(() => {
    // CRITICAL FIX: Don't hydrate if the user is actively editing or has pending inputs
    const hasActivePendingInputs = Object.keys(inputValues).some(activity =>
      Object.keys(inputValues[activity] || {}).length > 0
    )

    if (hasActivePendingInputs || isUserEditingRef.current) {
      console.log('[HYDRATE] BLOCKED - User has pending inputs or is actively editing, skipping hydration to prevent input reset')
      return
    }

    // Set flag to prevent sync effects from running during hydration
    isHydratingRef.current = true

    // Allow hydration to run if we have new data from the backend, even if already initialized
    // This ensures data persists when the user navigates back to this screen

    // Check multiple data sources for FTE allocation data
    const taskFTEAllocation = data.taskFTEAllocation || data.summary_data?.taskFTEAllocation
    const regularFTEAllocation = data.fteAllocation || data.summary_data?.fteAllocation

    // Only use taskFTEAllocation if it has actual FTE data (fteMatrix), otherwise fall back to legacy format
    const hasTaskFTEData = taskFTEAllocation && (taskFTEAllocation.fteMatrix || (taskFTEAllocation.customActivities && taskFTEAllocation.customActivities.length > 0))
    const fteAllocationData = hasTaskFTEData ? taskFTEAllocation : regularFTEAllocation

    console.log('[HYDRATE] FTE Allocation data sources:', {
      hasTaskFTEAllocation: !!taskFTEAllocation,
      hasRegularFTEAllocation: !!regularFTEAllocation,
      hasFinalFTEAllocation: !!fteAllocationData,
      taskFTEStructure: taskFTEAllocation ? Object.keys(taskFTEAllocation) : [],
      regularFTEStructure: regularFTEAllocation ? Object.keys(regularFTEAllocation) : [],
      taskFTEHasFteMatrix: !!(taskFTEAllocation?.fteMatrix),
      regularFTEHasRegions: !!(regularFTEAllocation?.fteAllocation?.regions),
      dataKeys: Object.keys(data),
      summaryDataKeys: data.summary_data ? Object.keys(data.summary_data) : []
    })

    if (fteAllocationData) {
      // Handle different data structures
      let regions = []

      if (fteAllocationData.fteMatrix) {
        // New format with fteMatrix
        const matrix = fteAllocationData.fteMatrix
        const matrixCosts = fteAllocationData.fteCostsByRegion || {}
        const matrixCustoms = fteAllocationData.customActivities || []

        // Ensure all custom activities have matrix entries
        const matrixWithCustomEntries = { ...matrix }
        matrixCustoms.forEach((activity: {id: string, name: string, frequency: 'Daily' | 'Monthly', group: string}) => {
          if (!matrixWithCustomEntries[activity.name]) {
            console.warn('[HYDRATE] Custom activity missing from matrix, creating entry:', activity.name)
            matrixWithCustomEntries[activity.name] = Object.fromEntries(REGIONS.map(region => [region, 0]))
          }
        })

        setFTEMatrix(matrixWithCustomEntries)
        // Load totalFTEsPerRegion if available
        if (fteAllocationData.totalFTEsPerRegion) {
          setTotalFTEsPerRegion(fteAllocationData.totalFTEsPerRegion)
        }
        setFteCostsByRegion({ ...fteCostsByRegion, ...matrixCosts })

        // Merge custom activities from server with local state, respecting removedActivityIds
        // This prevents overwriting newly added or removed activities

        // First merge removed IDs from server and local
        const serverRemovedIds = fteAllocationData.removedActivityIds || [];
        const mergedRemovedIds = Array.from(new Set([...removedActivityIds, ...serverRemovedIds]));

        // Update removedActivityIds if server has any to merge
        if (serverRemovedIds.length > 0 && mergedRemovedIds.length > removedActivityIds.length) {
          setRemovedActivityIds(mergedRemovedIds);
        }

        // Filter server activities to exclude removed ones
        const filteredServerCustoms = matrixCustoms.filter((activity: {id: string, name: string, frequency: 'Daily' | 'Monthly', group: string}) =>
          !mergedRemovedIds.includes(activity.id)
        );

        // Only update customActivities from server if server has MORE (after filtering) than local, or local is empty.
        // CRITICAL: Compare filtered count (not raw) to avoid overwriting local additions with a smaller server set.
        if (filteredServerCustoms.length > customActivities.length || customActivities.length === 0) {
          if (filteredServerCustoms.length > 0 || customActivities.length === 0) {
            const sameCustomActivities =
              filteredServerCustoms.length === customActivities.length &&
              filteredServerCustoms.every((activity: { id: string }, index: number) => activity.id === customActivities[index]?.id);
            if (!sameCustomActivities) {
              setCustomActivities(filteredServerCustoms);
            }
            console.log('[HYDRATE] Updated custom activities from server:', {
              serverCount: matrixCustoms.length,
              filteredCount: filteredServerCustoms.length,
              localCount: customActivities.length,
              removedIds: mergedRemovedIds
            });
          } else {
            console.log('[HYDRATE] All server activities have been removed, keeping local state');
          }
        } else {
          console.log('[HYDRATE] Keeping local customActivities (local has more or equal):', {
            serverCount: matrixCustoms.length,
            filteredCount: filteredServerCustoms.length,
            localCount: customActivities.length
          });
        }

        // Also load automation percentages if available
        if (fteAllocationData.automationPercentages) {
          setAutomationPercentages(fteAllocationData.automationPercentages)
        }

        setHasInitialized(true)
        if (!hasHydratedRef.current) {
          hasHydratedRef.current = true;
          setHasLocalChanges(false);
        }
        return // Exit early since we loaded everything
      } else if (fteAllocationData.regions || fteAllocationData.fteAllocation?.regions) {
        // Legacy format with regions array
        regions = fteAllocationData.regions || fteAllocationData.fteAllocation?.regions || []

        // CRITICAL FIX: Extract totalFTEs from legacy regions format
        const legacyTotals: Record<string, number> = {}
        regions.forEach((region: any) => {
          if (region.regionName && typeof region.totalFTEs === 'number') {
            legacyTotals[region.regionName] = region.totalFTEs
          }
        })

        if (Object.keys(legacyTotals).length > 0) {
          console.log('[HYDRATE] Extracted totalFTEs from legacy regions format:', legacyTotals)
          setTotalFTEsPerRegion(legacyTotals)
        }
        console.log('[HYDRATE] Loading from regions format:', { regionsCount: regions.length })
      }

      if (regions.length > 0) {
        const matrix: Record<string, Record<string, number>> = {}
        const totals: Record<string, number> = {}

        regions.forEach((region: any) => {
          totals[region.regionName] = region.totalFTEs || 0
          Object.entries(region.activityBreakdown || {}).forEach(([activity, ftes]) => {
            if (!matrix[activity]) matrix[activity] = {}
            matrix[activity][region.regionName] = ftes as number
          })
        })

        setFTEMatrix(matrix)
        setTotalFTEsPerRegion(totals)
        setHasInitialized(true)
        if (!hasHydratedRef.current) {
          hasHydratedRef.current = true;
          setHasLocalChanges(false);
        }
        console.log('[HYDRATE] Task-first FTE Allocation - loaded from regions:', {
          matrixKeys: Object.keys(matrix),
          totalsKeys: Object.keys(totals),
          sampleActivity: Object.keys(matrix)[0],
          sampleRegionData: matrix[Object.keys(matrix)[0]]
        })
      }
    } else if (Object.keys(fteMatrix).length === 0) {
      // Only initialize empty matrix if current matrix is actually empty
      const matrix: Record<string, Record<string, number>> = {}
      R2R_ACTIVITIES.forEach(activity => {
        matrix[activity.activityName] = {}
        REGIONS.forEach(region => {
          matrix[activity.activityName][region] = 0
        })
      })
      setFTEMatrix(matrix)
      setHasInitialized(true)
      if (!hasHydratedRef.current) {
        hasHydratedRef.current = true;
        setHasLocalChanges(false);
      }
      console.log('[HYDRATE] No existing data - initialized empty matrix')
    }

    // Clear hydration flag immediately - React will batch state updates anyway
    isHydratingRef.current = false
  }, [data.taskFTEAllocation, data.fteAllocation, data.summary_data?.taskFTEAllocation, data.summary_data?.fteAllocation])

  // Public-mode hydration directly via token endpoint to ensure state without private context
  useEffect(() => {
    if (!isPublic || !publicToken) return;
    (async () => {
      try {
        const res = await fetch(`/api/public/value-assessment/${publicToken}`)
        if (!res.ok) return;
        const json = await res.json();
        const sd = (json && json.data && json.data.summary_data) || {};
        const task = sd.taskFTEAllocation || {};
        const legacy = sd.fteAllocation || {};

        if (task && task.fteMatrix) {
          setFTEMatrix(task.fteMatrix || {});
          if (task.totalFTEsPerRegion) setTotalFTEsPerRegion(task.totalFTEsPerRegion);
          if (task.fteCostsByRegion) setFteCostsByRegion(prev => ({ ...prev, ...task.fteCostsByRegion }));
          if (task.automationPercentages) setAutomationPercentages(task.automationPercentages);

          // Merge removedActivityIds and filter custom activities
          const serverRemovedIds = task.removedActivityIds || [];
          const mergedRemovedIds = Array.from(new Set([...removedActivityIds, ...serverRemovedIds]));

          // Update removedActivityIds if server has any to merge
          if (serverRemovedIds.length > 0 && mergedRemovedIds.length > removedActivityIds.length) {
            setRemovedActivityIds(mergedRemovedIds);
          }

          // Filter custom activities by merged removedActivityIds before setting
        if (Array.isArray(task.customActivities)) {
          const filteredActivities = task.customActivities.filter((activity: {id: string}) =>
            !mergedRemovedIds.includes(activity.id)
          );
          // Only update if server has more than local (after filtering) to avoid overwriting local additions
          if (filteredActivities.length > customActivities.length || customActivities.length === 0) {
            setCustomActivities(filteredActivities);
          }
        }

        setHasInitialized(true);
        if (!hasHydratedRef.current) {
          hasHydratedRef.current = true;
          setHasLocalChanges(false);
        }
        return;
      }

        const regions = (legacy && (legacy.regions || legacy.fteAllocation?.regions)) || [];
        if (regions && regions.length > 0) {
          const matrix: Record<string, Record<string, number>> = {};
          const totals: Record<string, number> = {};
          regions.forEach((region: any) => {
            totals[region.regionName] = region.totalFTEs || 0;
            Object.entries(region.activityBreakdown || {}).forEach(([activity, ftes]) => {
              if (!matrix[activity]) matrix[activity] = {};
              matrix[activity][region.regionName] = ftes as number;
            });
          });
        setFTEMatrix(matrix);
        setTotalFTEsPerRegion(totals);
        setHasInitialized(true);
        if (!hasHydratedRef.current) {
          hasHydratedRef.current = true;
          setHasLocalChanges(false);
        }
      }
      } catch (e) {
        console.error('[PUBLIC LOAD] Failed to hydrate allocation via token', e);
      }
    })();
  }, [isPublic, publicToken]);

  // Initialize expanded groups only once
  useEffect(() => {
    if (Object.keys(expandedGroups).length === 0) {
      const initialExpanded: Record<string, boolean> = {}
      getTaskGroups().forEach(group => {
        initialExpanded[group] = true
      })
      setExpandedGroups(initialExpanded)
    }
  }, [])

  // Hydrate automation percentages from valueModeling (fallback only)
  // NOTE: taskFTEAllocation.automationPercentages is the authoritative source (loaded in Effect 1 above).
  // This effect is a fallback for legacy data where automationPercentages only exist in valueModeling.
  useEffect(() => {
    // Don't overwrite if we already loaded from taskFTEAllocation (the authoritative source)
    if (Object.keys(automationPercentages).length > 0) {
      console.log('🤖 [HYDRATION] Skipping valueModeling hydration — automationPercentages already loaded from taskFTEAllocation')
      return
    }

    if (data.summary_data?.valueModeling?.automationLevels && Array.isArray(data.summary_data.valueModeling.automationLevels)) {
      const automationLevels = data.summary_data.valueModeling.automationLevels

      console.log('🤖 [HYDRATION] Loading automation data from valueModeling (fallback):', {
        automationItemsCount: automationLevels.length,
        activities: automationLevels.map((item: any) => `${item.activity || item.name}: ${item.clientPredictedRate ?? item.automationPercentage ?? item.percentage}%`)
      })

      const percentages: Record<string, number> = {}

      automationLevels.forEach((item: any) => {
        const activity = item.activity || item.name
        const percentage = item.clientPredictedRate || item.automationPercentage || item.percentage || 0

        if (activity) {
          percentages[activity] = percentage
        }
      })

      setAutomationPercentages(percentages)
      console.log("✅ [HYDRATION] Automation percentages loaded from valueModeling fallback")
    }
  }, [data.summary_data?.valueModeling?.automationLevels])

  // Calculate regional totals when matrix changes
  useEffect(() => {
    const totals: Record<string, number> = {}
    REGIONS.forEach(region => {
      totals[region] = Object.values(fteMatrix).reduce((sum, activityFTEs) => {
        return sum + (activityFTEs[region] || 0)
      }, 0)
    })
    setRegionalTotals(totals)
  }, [fteMatrix])

  // Keep context's taskFTEAllocation in sync with Regional FTE Totals and costs so backend merges never drop them
  useEffect(() => {
    // CRITICAL: Skip during hydration to prevent infinite loop
    if (isHydratingRef.current) {
      console.log('[SYNC] Skipped - hydration in progress')
      return
    }

    // CRITICAL: Only update if values have actually changed to prevent infinite loop
    const contextTotals = data.taskFTEAllocation?.totalFTEsPerRegion || {}
    const contextCosts = data.taskFTEAllocation?.fteCostsByRegion || {}

    const totalsChanged = JSON.stringify(contextTotals) !== JSON.stringify(totalFTEsPerRegion)
    const costsChanged = JSON.stringify(contextCosts) !== JSON.stringify(fteCostsByRegion)

    if (!totalsChanged && !costsChanged) {
      console.log('[SYNC] Skipped - no changes detected')
      return
    }

    console.log('[SYNC] Updating context with new values', { totalsChanged, costsChanged })
    updateSection('taskFTEAllocation', {
      totalFTEsPerRegion: { ...totalFTEsPerRegion },
      fteCostsByRegion: { ...fteCostsByRegion }
    }, { autoSave: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(totalFTEsPerRegion), JSON.stringify(fteCostsByRegion)]);

  // Clean up inputValues when fteMatrix is updated
  useEffect(() => {
    // Clear inputValues that have been committed to fteMatrix
    setInputValues(prev => {
      const newInputValues = { ...prev }
      Object.keys(newInputValues).forEach(activity => {
        Object.keys(newInputValues[activity] || {}).forEach(region => {
          if (fteMatrix[activity]?.[region] === parseFloat(newInputValues[activity][region])) {
            delete newInputValues[activity][region]
            if (Object.keys(newInputValues[activity]).length === 0) {
              delete newInputValues[activity]
            }
          }
        })
      })
      return newInputValues
    })
  }, [fteMatrix])

  // Memoized pillar totals per group/region
  const pillarTotals = useMemo(() => {
    const totals: Record<string, Record<string, number>> = {};

    getTaskGroups().forEach(group => {
      const activities = getActivitiesByGroup(group);
      totals[group] = {};

      REGIONS.forEach(region => {
        const sum = activities.reduce((acc, activity) => {
          const fte = fteMatrix[activity.activityName]?.[region] || 0;
          return acc + fte;
        }, 0);
        totals[group][region] = sum;
      });
    });

    return totals;
  }, [fteMatrix]);

  // Mixed allocated totals by region for badges/validation
  // Uses pillarFTEAllocation for groups in pillar view, and fteMatrix sums for groups in activity view
  const allocatedTotalsByRegion = useMemo(() => {
    const totals: Record<string, number> = {};
    REGIONS.forEach(region => { totals[region] = 0; });

    getTaskGroups().forEach(group => {
      const mode: 'activity' | 'pillar' = viewMode[group] || 'activity';
      if (mode === 'pillar' && pillarFTEAllocation[group]) {
        REGIONS.forEach(region => {
          totals[region] += pillarFTEAllocation[group]?.ftesByRegion?.[region] || 0;
        });
      } else {
        const activities = getActivitiesByGroup(group);
        REGIONS.forEach(region => {
          const sum = activities.reduce((acc, activity) => acc + (fteMatrix[activity.activityName]?.[region] || 0), 0);
          totals[region] += sum;
        });
      }
    });

    // Include custom activities in the allocation totals
    customActivities.forEach(activity => {
      REGIONS.forEach(region => {
        totals[region] += fteMatrix[activity.name]?.[region] || 0;
      });
    });

    return totals;
  }, [viewMode, pillarFTEAllocation, fteMatrix, customActivities]);

  // Update FTE allocation for specific activity and region (legacy function, kept for other uses)
  const updateFTEAllocation = (activityName: string, regionName: string, ftes: number) => {
    setFTEMatrix(prev => ({
      ...prev,
      [activityName]: {
        ...prev[activityName],
        [regionName]: ftes
      }
    }))
    markUnsaved();
  }

  // Helper: clear all By Activity cells for a group's activities and optionally return the new matrix
  const buildClearedMatrixForGroup = (group: string, source: Record<string, Record<string, number>>) => {
    const next = { ...source } as Record<string, Record<string, number>>;
    const activities = getActivitiesByGroup(group);
    activities.forEach(activity => {
      const name = activity.activityName;
      const current = next[name] || {};
      const cleared: Record<string, number> = {};
      REGIONS.forEach(r => { cleared[r] = 0; });
      next[name] = { ...current, ...cleared };
    });
    return next;
  };

  const clearGroupActivitiesInMatrix = (group: string) => {
    setFTEMatrix(prev => buildClearedMatrixForGroup(group, prev));
  };

  // Defer provider updates to avoid setState during render warnings
  const safeUpdateSection = (section: Parameters<typeof updateSection>[0], payload: Parameters<typeof updateSection>[1]) => {
    markUnsaved();
    setTimeout(() => {
      // Double-guard in case component unmounted
      try { updateSection(section, payload, { autoSave: false }); } catch {}
    }, 0);
  };

  // Handlers for Pillar-level editing
  const handlePillarFTEChange = (group: string, region: string, value: string) => {
    preventHydrationTemporarily();
    const num = isNaN(parseFloat(value)) ? 0 : parseFloat(value);
    setPillarFTEAllocation(prev => {
      const current = prev[group] || { ftesByRegion: {}, automationPercentage: 0 };
      const nextGroup = {
        ...current,
        ftesByRegion: {
          ...current.ftesByRegion,
          [region]: num
        }
      };
      const next = { ...prev, [group]: nextGroup };
      // Defer persistence to avoid setState during render
      safeUpdateSection('taskFTEAllocation', { 
        pillarFTEAllocation: next, 
        allocationMode: 'pillar',
        // Preserve Regional FTE Totals and other critical fields to prevent server overwrite
        totalFTEsPerRegion: { ...totalFTEsPerRegion },
        fteCostsByRegion: { ...fteCostsByRegion },
        regionalTotals: { ...regionalTotals },
        fteMatrix: { ...fteMatrix },
        customActivities: [...customActivities],
        automationPercentages: { ...automationPercentages },
        rampByPillarYear,
        rationaleOverrides
      });
      return next;
    });
    // Empty the corresponding group's By Activity entries in UI state after a pillar edit
    clearGroupActivitiesInMatrix(group);
    markUnsaved();
  };

  const handlePillarAutomationChange = (group: string, value: string) => {
    preventHydrationTemporarily();
    const num = Math.max(0, Math.min(100, parseInt(value) || 0));
    setPillarFTEAllocation(prev => {
      const current = prev[group] || { ftesByRegion: {}, automationPercentage: 0 };
      const nextGroup = { ...current, automationPercentage: num };
      const next = { ...prev, [group]: nextGroup };
      safeUpdateSection('taskFTEAllocation', { 
        pillarFTEAllocation: next, 
        allocationMode: 'pillar',
        totalFTEsPerRegion: { ...totalFTEsPerRegion },
        fteCostsByRegion: { ...fteCostsByRegion },
        regionalTotals: { ...regionalTotals },
        fteMatrix: { ...fteMatrix },
        customActivities: [...customActivities],
        automationPercentages: { ...automationPercentages },
        rampByPillarYear,
        rationaleOverrides
      });
      return next;
    });
    // Also clear By Activity rows for this group to ensure pillar-only data applies
    clearGroupActivitiesInMatrix(group);
    markUnsaved();
  };

  const handleRampChange = (group: string, yearIndex: number, value: string) => {
    const parsed = Number.parseInt(value, 10);
    const clamped = Number.isFinite(parsed) ? Math.max(0, Math.min(100, parsed)) : 0;

    setRampByPillarYear(prev => {
      const current = prev[group] || [...DEFAULT_RAMP_VECTOR] as RampVector;
      const nextVector = [...current] as number[];
      nextVector[yearIndex] = clamped;
      const next = {
        ...prev,
        [group]: nextVector as RampVector
      };

      safeUpdateSection('taskFTEAllocation', {
        pillarFTEAllocation,
        rampByPillarYear: next,
        rationaleOverrides,
        allocationMode: 'pillar',
        totalFTEsPerRegion: { ...totalFTEsPerRegion },
        fteCostsByRegion: { ...fteCostsByRegion },
        regionalTotals: { ...regionalTotals },
        fteMatrix: { ...fteMatrix },
        customActivities: [...customActivities],
        automationPercentages: { ...automationPercentages }
      });
      return next;
    });

    markUnsaved();
  };

  // Sync global allocationMode with per-group viewMode (any pillar -> pillar)
  // SAFE-GUARD: Do not force-switch immediately after user explicitly clicks a mode button.
  const lastModeChangeRef = useRef<number>(0);
  useEffect(() => {
    if (!viewMode || Object.keys(viewMode).length === 0) return;
    const anyPillar = Object.values(viewMode).includes('pillar');
    const newMode: 'activity' | 'pillar' = anyPillar ? 'pillar' : 'activity';
    const now = Date.now();
    // Only auto-sync if no explicit mode change within last 1500ms
    if (allocationMode !== newMode && now - lastModeChangeRef.current > 1500) {
      setAllocationMode(newMode);
      updateSection('taskFTEAllocation', { 
        allocationMode: newMode,
        totalFTEsPerRegion: { ...totalFTEsPerRegion },
        fteCostsByRegion: { ...fteCostsByRegion },
        regionalTotals: { ...regionalTotals },
        fteMatrix: { ...fteMatrix },
        customActivities: [...customActivities],
        automationPercentages: { ...automationPercentages },
        rampByPillarYear,
        rationaleOverrides
      }, { autoSave: false });
    }
  }, [viewMode]);

  // Persist FTE allocation to valueSummary blob on demand
  const persistTaskFTEAllocation = useCallback(async (options?: SaveSummarySectionOptions) => {
    if (!projectId) return;

    const regions = REGIONS.map(regionName => ({
      regionName,
      totalFTEs: totalFTEsPerRegion[regionName] || 0,
      activityBreakdown: Object.fromEntries(
        Object.entries(fteMatrix).map(([activity, regionFTEs]) => [
          activity,
          regionFTEs[regionName] || 0
        ])
      )
    }));

    let allRegionsValid = true;
    let totalAllocated = 0;
    let totalAvailable = 0;

    REGIONS.forEach(region => {
      const allocated = regionalTotals[region] || 0;
      const available = totalFTEsPerRegion[region] || 0;
      totalAllocated += allocated;
      totalAvailable += available;

      if (available > 0 && (allocated / available) < 0.9) {
        allRegionsValid = false;
      }
    });

    const overallPercentage = totalAvailable > 0 ? Math.round((totalAllocated / totalAvailable) * 100) : 0;
    const validationStatus = allRegionsValid ? 'valid' : 'under-allocated';
    const allocationComplete = allRegionsValid;

    const fteAllocationData: FTEAllocationData = {
      regions,
      validationStatus,
      allocationComplete,
      overallAllocation: `${overallPercentage}%`
    };

    const enhancedFTEData = {
      fteAllocation: fteAllocationData,
      fteCostsByRegion: fteCostsByRegion,
      totalAllocatedFTEs: Object.values(regionalTotals).reduce((sum, total) => sum + total, 0),
      totalAvailableFTEs: Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0),
      regionMapping: Object.fromEntries(
        REGIONS.map(region => [region, {
          allocated: regionalTotals[region] || 0,
          available: totalFTEsPerRegion[region] || 0,
          averageCost: fteCostsByRegion[region] || 0
        }])
      )
    };

    const allocationData = {
      allocationMode,
      pillarFTEAllocation,
      rampByPillarYear,
      rationaleOverrides,
      fteMatrix,
      regionalTotals,
      totalFTEsPerRegion,
      fteCostsByRegion,
      customActivities,
      automationPercentages,
      removedActivityIds
    };

    const automationLevels = Object.entries(automationPercentages)
      .filter(([_, percentage]) => percentage > 0)
      .map(([activity, percentage]) => ({
        activity,
        automationPercentage: percentage,
        name: activity,
        percentage
      }));

    const schemaVersion = (allocationMode === 'pillar' || Object.keys(pillarFTEAllocation || {}).length > 0) ? 'v2.2' : 'v2.1';

    await saveSummarySection({
      fteAllocation: enhancedFTEData,
      taskFTEAllocation: allocationData,
      valueModeling: {
        automationLevels,
        lastUpdated: new Date().toISOString()
      },
      valueSummary: {
        taskFTEAllocation: allocationData,
        rampByPillarYear,
        rationaleOverrides
      },
      lastUpdatedAt: new Date().toISOString(),
      schemaVersion
    }, options);

    updateSection('taskFTEAllocation', allocationData, { autoSave: false });
    updateSection('fteAllocation', fteAllocationData, { autoSave: false });
    setHasLocalChanges(false);

    console.log('[SAVE] Task-first FTE Allocation persisted:', {
      validationStatus,
      allocationComplete,
      overallAllocation: `${overallPercentage}%`
    });
  }, [
    allocationMode,
    automationPercentages,
    customActivities,
    fteCostsByRegion,
    fteMatrix,
    projectId,
    regionalTotals,
    removedActivityIds,
    saveSummarySection,
    totalFTEsPerRegion,
    pillarFTEAllocation,
    rampByPillarYear,
    rationaleOverrides,
    updateSection
  ]);

  // FIXED: Handle input changes during typing (string state only)
  const handleFTEInputChange = (activityName: string, regionName: string, value: string) => {
    // isUserEditingRef.current = true
    preventHydrationTemporarily();
    setInputValues(prev => ({
      ...prev,
      [activityName]: {
        ...prev[activityName],
        [regionName]: value
      }
    }))
  }

  // FIXED: Handle input blur to convert to numbers and update local state only
  const handleFTEInputBlur = (activityName: string, regionName: string, value: string) => {
    const ftes = parseFloat(value) || 0

    // Update local state immediately - auto-save will handle persistence
    setFTEMatrix(prev => ({
      ...prev,
      [activityName]: {
        ...prev[activityName],
        [regionName]: ftes
      }
    }))

    // Clear the input value since we've committed it to the matrix
    setInputValues(prev => {
      const newInputValues = { ...prev }
      if (newInputValues[activityName]) {
        delete newInputValues[activityName][regionName]
        if (Object.keys(newInputValues[activityName]).length === 0) {
          delete newInputValues[activityName]
        }
      }
      return newInputValues
    })

    // Allow hydration to run again after a short delay
    preventHydrationTemporarily();
    markUnsaved();
  }

  const handleTotalFTEInput = (regionName: string, value: string) => {
    preventHydrationTemporarily();
    const total = parseInt(value) || 0
    setTotalFTEsPerRegion(prev => ({
      ...prev,
      [regionName]: total
    }));
    markUnsaved();
  }

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }))
  }

  const addCustomActivity = () => {
    preventHydrationTemporarily();

    const activityId = `custom_${Date.now()}`
    const customName = `Custom Activity ${customActivities.length + 1}`
    const newActivity = {
      id: activityId,
      name: customName,
      frequency: 'Monthly' as const,
      group: 'Other'
    }

    console.log('[ADD] Adding new custom activity:', newActivity)

    setCustomActivities(prev => {
      const updated = [...prev, newActivity]
      console.log('[ADD] Updated customActivities:', updated)
      return updated
    })

    markUnsaved();

    // Initialize matrix entry for custom activity
    setFTEMatrix(prev => {
      const updated = {
        ...prev,
        [customName]: Object.fromEntries(REGIONS.map(region => [region, 0]))
      }
      console.log('[ADD] Added matrix entry for:', customName)
      console.log('[ADD] Updated matrix keys:', Object.keys(updated))
      return updated
    })
  }

  const updateCustomActivityName = (activityId: string, newName: string) => {
    preventHydrationTemporarily();

    // Find old activity BEFORE updating state
    const oldActivity = customActivities.find(a => a.id === activityId)
    const oldName = oldActivity?.name

    console.log('[RENAME] Renaming custom activity:', { activityId, oldName, newName })

    // Update customActivities state
    setCustomActivities(prev => prev.map(activity =>
      activity.id === activityId
        ? { ...activity, name: newName }
        : activity
    ))
    markUnsaved();

    // Update matrix key if name changed
    if (oldName && oldName !== newName) {
      setFTEMatrix(prev => {
        const newMatrix = { ...prev }

        console.log('[RENAME] Current matrix keys:', Object.keys(newMatrix))
        console.log('[RENAME] Looking for old key:', oldName)
        console.log('[RENAME] Matrix has old key:', oldName in newMatrix)

        if (newMatrix[oldName]) {
          // Copy the data to the new key
          newMatrix[newName] = newMatrix[oldName]
          // Delete the old key
          delete newMatrix[oldName]
          console.log('[RENAME] Successfully renamed matrix key from', oldName, 'to', newName)
        } else {
          // Old key doesn't exist, create new entry with zeros
          console.warn('[RENAME] Old matrix key not found, creating new entry:', newName)
          newMatrix[newName] = Object.fromEntries(REGIONS.map(region => [region, 0]))
        }

        console.log('[RENAME] Updated matrix keys:', Object.keys(newMatrix))
        return newMatrix
      })
    }
  }

  const updateCustomActivityFrequency = (activityId: string, frequency: 'Daily' | 'Monthly') => {
    setCustomActivities(prev => prev.map(activity =>
      activity.id === activityId
        ? { ...activity, frequency }
        : activity
    ))
    markUnsaved();
  }

  const removeCustomActivity = (activityId: string) => {
    // Prevent hydration from overwriting our changes during removal
    preventHydrationTemporarily();

    console.log('[REMOVE] Starting removal of custom activity with ID:', activityId)
    console.log('[REMOVE] Current customActivities:', customActivities)

    const activityToRemove = customActivities.find(a => a.id === activityId)
    console.log('[REMOVE] Activity to remove:', activityToRemove)

    if (activityToRemove) {
      console.log('[REMOVE] Found activity to remove:', activityToRemove.name)

      // Store the activity name before removing it from state
      const activityName = activityToRemove.name

      // Log before state update
      console.log('[REMOVE] Current customActivities before update:', customActivities)
      console.log('[REMOVE] Current fteMatrix before update:', fteMatrix)

      // Update customActivities state - use a temporary variable to ensure the update is applied
      const updatedActivities = customActivities.filter(a => a.id !== activityId)
      console.log('[REMOVE] Filtered customActivities:', updatedActivities)
      setCustomActivities(updatedActivities)

      // Add the activity ID to the removedActivityIds state to prevent it from being re-added during hydration
      setRemovedActivityIds(prev => {
        const updated = [...prev, activityId];
        console.log('[REMOVE] Added activity ID to removedActivityIds:', activityId);
        return updated;
      })

      // Remove from matrix - use a temporary variable to ensure the update is applied
      const updatedMatrix = { ...fteMatrix }
      console.log('[REMOVE] Activity name to remove from matrix:', activityName)
      console.log('[REMOVE] Matrix has this activity:', activityName in updatedMatrix)

      if (activityName in updatedMatrix) {
        delete updatedMatrix[activityName]
        console.log('[REMOVE] Updated matrix after removal:', updatedMatrix)
        setFTEMatrix(updatedMatrix)
      } else {
        console.warn('[REMOVE] Activity not found in matrix:', activityName)
        // Try to find a similar key in the matrix (in case of name mismatch)
        const possibleKeys = Object.keys(fteMatrix)
        console.log('[REMOVE] Possible matrix keys:', possibleKeys)

        // Check if there's a key that contains the activity name or vice versa
        const similarKey = possibleKeys.find(key =>
          key.includes(activityName) || activityName.includes(key)
        )

        if (similarKey) {
          console.log('[REMOVE] Found similar key in matrix:', similarKey)
          delete updatedMatrix[similarKey]
          setFTEMatrix(updatedMatrix)
        }
      }

      // Log after state updates are queued
      console.log('[REMOVE] State updates applied for customActivities and fteMatrix')
      console.log('[REMOVE] Updated customActivities:', updatedActivities)
      console.log('[REMOVE] Updated fteMatrix:', updatedMatrix)

      // Force a re-render by updating a related state
      setRegionalTotals(prev => ({...prev}))

    } else {
      console.warn('[REMOVE] Activity with ID not found:', activityId)
    }

    markUnsaved();
  }

  // Update FTE cost by region - NEW FUNCTION for regional FTE costs
  const updateFTECost = (regionName: string, cost: number) => {
    preventHydrationTemporarily();
    setFteCostsByRegion(prev => ({
      ...prev,
      [regionName]: cost
    }))
    markUnsaved();
  }

  const getValidationStatus = () => {
    // Check each region's allocation percentage (90% requirement)
    const underAllocatedRegions: string[] = []

    REGIONS.forEach(region => {
      const allocated = allocatedTotalsByRegion[region] || 0
      const available = totalFTEsPerRegion[region] || 0

      if (available > 0) {
        const allocationPercent = (allocated / available) * 100
        if (allocationPercent < 90) {
          underAllocatedRegions.push(region) // Just push the region name
        }
      }
    })

    return underAllocatedRegions
  }

  const handleContinue = async () => {
    const underAllocatedRegions = getValidationStatus()

    if (underAllocatedRegions.length > 0) {
      console.warn('[VALIDATION] Cannot continue - under-allocated regions:', underAllocatedRegions)
      return
    }

    // Validate automation percentages - EVERY task with FTE data must have automation > 0
    const tasksWithFTEs = Object.entries(fteMatrix).filter(([_, regionData]) =>
      Object.values(regionData).some(fte => fte > 0)
    ).map(([taskName]) => taskName)

    const tasksWithoutAutomation = tasksWithFTEs.filter(taskName =>
      automationPercentages[taskName] == null
    )

    if (tasksWithoutAutomation.length > 0) {
      console.warn('[VALIDATION] Cannot continue - tasks missing automation percentages:', tasksWithoutAutomation)
      alert(`Please set automation percentages for ALL tasks with FTE allocations.\n\nMissing automation for:\n${tasksWithoutAutomation.map(t => `• ${t}`).join('\n')}\n\nAutomation percentages represent the expected time savings from automation.`)
      return
    }

    try {
      if (hasLocalChanges) {
        await persistTaskFTEAllocation();
      }
    } catch (error) {
      console.error('❌ [SAVE] Failed to persist Task FTE Allocation before continuing:', error);
      return;
    }

    console.log('✅ Task-first FTE Allocation screen rendered with groups and frequencies')
    console.log('✅ 90%+ validation working per region')
    console.log('✅ valueSummary.fteAllocation =', {
      regions: REGIONS.map(regionName => ({
        regionName,
        totalFTEs: totalFTEsPerRegion[regionName] || 0,
        activityBreakdown: Object.fromEntries(
          Object.entries(fteMatrix).map(([activity, regionFTEs]) => [
            activity,
            regionFTEs[regionName] || 0
          ])
        )
      }))
    })

    // FLOW DISTINCTION FIX: Check if this is external shareable link vs internal assessment
    const urlParams = new URLSearchParams(window.location.search)
    const isPublicSharableLink = urlParams.get('public') === 'true'

    console.log('🔍 FLOW ROUTING ANALYSIS:', {
      isPublicSharableLink,
      hasPublicParam: urlParams.get('public'),
      currentURL: window.location.href,
      projectId
    })

    if (isPublicSharableLink) {
      // EXTERNAL SHAREABLE LINK: Complete after 3 screens (Kickoff, Context, Allocation)
      console.log('✅ External shareable link flow - showing completion screen')
      sessionStorage.setItem('publicClientFlow', 'true')
      setShowCompletionMessage(true)
    } else {
      // INTERNAL ASSESSMENT: Check project pricing settings to determine next screen
      console.log('🔍 Internal assessment flow - checking pricing settings...')

      try {
        const project = await apiRequest(`/api/projects/${projectId}`)
        const hasPricingEnabled = !!(project as any).value_assessment_pricing_enabled

        console.log('🔍 PROJECT ROUTING: Automation step removed from flow')

        // FLOW UPDATE: Always continue to Platform Configuration (automation step removed)
        console.log('✅ Continuing to Platform Configuration (automation step skipped)')
        navigate(`/value-assessment/${projectId}/configuration`)

      } catch (error) {
        console.error('❌ Error checking project settings:', error)
        // Fallback to configuration screen (automation step removed)
        navigate(`/value-assessment/${projectId}/configuration`)
      }
    }
  }

  const handleBack = async () => {
    if (hasLocalChanges) {
      try {
        await persistTaskFTEAllocation();
      } catch (error) {
        console.error('❌ [SAVE] Failed to persist Task FTE Allocation before navigating back:', error);
        return;
      }
    }

    const urlParams = new URLSearchParams(window.location.search)
    const isPublicFlow = urlParams.get('public') === 'true' || urlParams.has('t')
    const t = urlParams.get('t')
    const backUrl = isPublicFlow
      ? `/value-assessment/${projectId}/context?public=true${t ? `&t=${t}` : ''}`
      : `/value-assessment/${projectId}/context`
    navigate(backUrl)
  }

  useUnsavedChangesWarning(hasLocalChanges, (options) => {
    persistTaskFTEAllocation(options).catch(() => {})
  })

  const underAllocatedRegions = getValidationStatus()

  // Validate automation percentages - EVERY task with FTE data must have automation > 0
  const tasksWithFTEs = Object.entries(fteMatrix).filter(([_, regionData]) =>
    Object.values(regionData).some(fte => fte > 0)
  ).map(([taskName]) => taskName)

  const tasksWithoutAutomation = tasksWithFTEs.filter(taskName =>
    automationPercentages[taskName] == null
  )

  const hasAllRequiredAutomation = tasksWithFTEs.length > 0 && tasksWithoutAutomation.length === 0
  const isValid = underAllocatedRegions.length === 0 && hasAllRequiredAutomation

  // If showing completion message, render that instead
  if (showCompletionMessage) {
    return (
      <div className="space-y-6">
        <Card className="rounded-lg border text-card-foreground shadow-sm bg-forestBlack/50 border-[#334155] backdrop-blur-sm">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center">
              <CheckCircle className="w-16 h-16 text-green-400" />
            </div>
            <CardTitle className="text-2xl font-bold text-white mb-2">
              Thank You for Your Input!
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <p className="text-gray-300 text-lg">
              Your project information has been successfully submitted. Our Redwood team now has all the data needed for the first 3 assessment screens.
            </p>

            <div className="grid md:grid-cols-3 gap-4 my-6">
              <div className="flex flex-col items-center p-4 bg-forestBlack rounded-lg border border-[#334155]">
                <Award className="w-8 h-8 text-primary mb-2" />
                <h3 className="font-semibold text-white mb-1">Project Kickoff</h3>
                <p className="text-sm text-gray-400">Complete</p>
              </div>
              <div className="flex flex-col items-center p-4 bg-forestBlack rounded-lg border border-[#334155]">
                <HandHeart className="w-8 h-8 text-primary mb-2" />
                <h3 className="font-semibold text-white mb-1">Strategic Context</h3>
                <p className="text-sm text-gray-400">Complete</p>
              </div>
              <div className="flex flex-col items-center p-4 bg-forestBlack rounded-lg border border-[#334155]">
                <Users className="w-6 h-6 text-primary mb-2" />
                <h3 className="font-semibold text-white mb-1">FTE Allocation</h3>
                <p className="text-sm text-gray-400">Complete</p>
              </div>
            </div>

            <div className="bg-forestBlack border border-[#334155] rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-white" />
                Next Steps
              </h3>
              <div className="text-left space-y-3 text-gray-300">
                <p>• A Redwood representative will review your submission within 1-2 business days</p>
                <p>• We'll schedule a follow-up session to continue the Value Assessment</p>
                <p>• Additional screens will be completed together during our consultation</p>
                <p>• You'll receive a comprehensive ROI analysis and strategic recommendations</p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-400">
                Questions? Contact us at <span className="text-primary">support@redwoodconsulting.com</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">FTE Allocation</h1>
          <p className="text-gray-300 mt-2">
            Allocate FTEs across R2R activities or by pillar per group. Switch modes per pillar (e.g., Account Reconciliation) using the toggle inside each section.
          </p>
        </div>
        <div className="text-sm text-gray-400">Step 3 of 6</div>
      </div>

      {/* Average FTE Costs by Region - COLLAPSIBLE */}
      <Collapsible
        open={showFTECosts}
        onOpenChange={setShowFTECosts}
      >
        <Card className="bg-forestBlack border-[#334155]">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-[#334155]/20 transition-colors">
              <CardTitle className="flex items-center justify-between text-[#F59E0B]">
                <div className="flex items-center gap-3">
                  {showFTECosts ? (
                    <ChevronDown className="w-5 h-5" />
                  ) : (
                    <ChevronRight className="w-5 h-5" />
                  )}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                  FTE Cost Settings
                </div>
                <div className="text-sm font-normal text-gray-300">
                  Avg. FTE cost: ${averageFTECost.toLocaleString()}
                  <span className="text-xs text-gray-400 ml-2">Click to view/edit assumptions per region</span>
                </div>
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <div className="space-y-4">
                <p className="text-gray-300 text-sm">Set the average annual FTE cost per region for accurate ROI calculations</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {REGIONS.map((region) => (
                    <div key={region}>
                      <Label className="text-gray-400 text-sm">{region}</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
                        <Input
                          type="text"
                          data-cy={`fte-cost-${region.toLowerCase().replace(/\s+/g, '-')}`}
                          value={fteCostsByRegion[region]?.toLocaleString() || '0'}
                          onChange={(e) => {
                            const value = e.target.value.replace(/,/g, '');
                            updateFTECost(region, parseInt(value) || 0);
                          }}
                          className="bg-redwoodInputFieldBg  border-[#334155] text-white pl-7"
                          placeholder="50000"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-gray-400">
                  These costs will be used for calculating potential savings from automation
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* ** Changes made to allocation screen 30 July ** */}
      {/* Page-level instruction block - restyled to match site */}
      <Card className="bg-forestBlack border border-[#334155]">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="text-2xl font-semibold text-white mb-3 flex items-center gap-2">
                <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Instructions
              </h3>
              <div className="space-y-2 text-gray-300">
                <p className="text-base">
                  Allocate your total FTEs across the regions listed below, then distribute those FTEs across the key Record-to-Report activities.
                </p>
                <p className="text-base">
                  You must allocate at least 90% of your regional FTEs to proceed.
                </p>
                <p className="text-base">
                  For each activity, set the percentage of work you believe can be automated using Redwood's Finance Automation platform.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* ** End of changes made to allocation screen 30 July ** */}

      {/* ** Changes made to allocation screen 30 July ** */}
      {/* Dynamic validation status moved above fold */}
      {(() => {
        const underAllocatedRegions = getValidationStatus();
        return underAllocatedRegions.length > 0 ? (
        <Card className="bg-amber-900/20 border-amber-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <div>
                <p className="text-orange font-medium">Allocation Incomplete</p>
                <p className="text-orange text-sm">
                  {underAllocatedRegions.map((region, idx) => (
                    <span key={region}>
                      {region}: {totalFTEsPerRegion[region] > 0 ? Math.round((allocatedTotalsByRegion[region] / totalFTEsPerRegion[region]) * 100) : 0}% allocated
                      {idx < underAllocatedRegions.length - 1 && ', '}
                    </span>
                  ))}
                </p>
                <p className="text-amber-300 text-xs mt-1">
                  Allocate at least 90% of FTEs per region to continue
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : Object.values(totalFTEsPerRegion).some(total => total > 0) && (
        <Card className="bg-green-900/20 border-primary">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-800" />
              <div>
                <p className="text-green-200 font-medium">Allocation Complete</p>
                <p className="text-green-300 text-sm">
                  All regions meet the 90% allocation requirement
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      );
      })()}

      {/* Automation Percentage Validation */}
      {tasksWithoutAutomation.length > 0 && (
        <Card className="bg-amber-900/20 border-amber-500 mt-4">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <div>
                <p className="text-orange font-medium">Automation Percentages Required</p>
                <p className="text-orange text-sm">
                  {tasksWithoutAutomation.length} task{tasksWithoutAutomation.length > 1 ? 's' : ''} with FTE allocations {tasksWithoutAutomation.length > 1 ? 'are' : 'is'} missing automation percentages:
                </p>
                <ul className="text-orange text-xs mt-2 ml-4 list-disc">
                  {tasksWithoutAutomation.slice(0, 5).map(task => (
                    <li key={task}>{task}</li>
                  ))}
                  {tasksWithoutAutomation.length > 5 && (
                    <li>...and {tasksWithoutAutomation.length - 5} more</li>
                  )}
                </ul>
                <p className="text-amber-300 text-xs mt-2">
                  Automation percentages represent the expected time savings from automation (e.g., 50% means half the work will be automated)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {/* ** End of changes made to allocation screen 30 July ** */}

      {/* Regional Totals Input - MOVED AFTER AVERAGE COSTS */}
      <Card className="bg-forestBlack border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            <Users className="w-5 h-5 text-primary" />
            Regional FTE Totals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-gray-300 text-sm">
              Please indicate where your Record-to-Report FTEs are located by region. This allocation defines your team capacity across the tasks below.
            </p>
            {/* ** Changes made to allocation screen 30 July ** */}
            {/* Aligned regional FTE totals to match activity grid below */}
            <div className="space-y-2">
              {/* Row 1: Labels */}
              <div className="grid grid-cols-12 gap-x-4 px-4">
                <div className="col-span-3"></div>
                <div className="col-span-1"></div>
                <div className="col-span-6 grid grid-cols-5">
                  {REGIONS.map(region => (
                    <div key={`label-${region}`} className="text-center">
                      <label className="block text-sm font-medium text-gray-300">
                        {region.toUpperCase()}
                      </label>
                    </div>
                  ))}
                </div>
                <div className="col-span-2"></div>
              </div>

              {/* Row 2: Input boxes */}
              <div className="grid grid-cols-12 gap-x-4 px-4">
                <div className="col-span-3"></div>
                <div className="col-span-1"></div>
                <div className="col-span-6 grid grid-cols-5">
                  {REGIONS.map(region => (
                    <div key={`input-${region}`} className="flex justify-center">
                      <Input
                        type="number"
                        value={totalFTEsPerRegion[region] || ''}
                        onChange={(e) => handleTotalFTEInput(region, e.target.value)}
                        placeholder="0"
                        className="w-16 h-10 text-right px-2 bg-redwoodInputFieldBg  border-[#334155] text-white text-sm"
                        data-cy={`fte-${region.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="col-span-2"></div>
              </div>

              {/* Row 3: Allocation status badges (only for regions with data) */}
              <div className="grid grid-cols-12 gap-x-4 px-4">
                <div className="col-span-3"></div>
                <div className="col-span-1"></div>
                <div className="col-span-6 grid grid-cols-5">
                  {REGIONS.map(region => (
                    <div key={`badge-${region}`} className="text-center" style={{ minHeight: '32px' }}>
                      {totalFTEsPerRegion[region] > 0 && (
                        <div
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                          style={{
                            backgroundColor: (allocatedTotalsByRegion[region] / totalFTEsPerRegion[region]) >= 0.9 ? 'rgba(34, 197, 94, 0.2)' : 'rgba(251, 146, 60, 0.2)',
                            border: `1px solid ${(allocatedTotalsByRegion[region] / totalFTEsPerRegion[region]) >= 0.9 ? '#34E5E2' : '#FFA943'}`
                          }}
                          title={`Allocated ${Math.round((allocatedTotalsByRegion[region] / totalFTEsPerRegion[region]) * 100)}%`}
                        >
                          {(allocatedTotalsByRegion[region] / totalFTEsPerRegion[region]) >= 0.9 ? (
                            <CheckCircle className="w-4 h-4" style={{ color: '#34E5E2' }} />
                          ) : (
                            <AlertTriangle className="w-4 h-4" style={{ color: '#FFA943' }} />
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="col-span-2"></div>
              </div>
            </div>
            {/* ** End of changes made to allocation screen 30 July ** */}
          </div>
        </CardContent>
      </Card>



      {/* Task Groups with FTE Matrix */}
      <div className="space-y-4">
        {getTaskGroups().map(taskGroup => {
          const activities = getActivitiesByGroup(taskGroup)
          const isExpanded = expandedGroups[taskGroup]

          return (
            <Card key={taskGroup} className="bg-forestBlack border-[#334155]">
              <Collapsible
                open={isExpanded}
                onOpenChange={() => toggleGroup(taskGroup)}
              >
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-[#334155]/20 transition-colors">
                    <CardTitle className="flex items-center justify-between text-white">
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-primary" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-primary" />
                        )}
                        <Users className="w-5 h-5 text-primary" />
                        {taskGroup}
                        <Badge variant="secondary" className="ml-2">
                          {activities.length} activities
                        </Badge>
                      </div>

                    </CardTitle>
                  </CardHeader>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <CardContent className="px-4 py-2">
                    {/* Per-group toggle for Activity vs Pillar */}
                    <div className="flex gap-4 items-center mt-4 mb-4">
                      <button
                        type="button"
                        data-cy={`allocation-mode-activity-${taskGroup.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        onClick={() => {
                          lastModeChangeRef.current = Date.now();
                          preventHydrationTemporarily();
                          // 1) Remove pillar allocation for this group entirely (so only Activity data remains authoritative)
                          setPillarFTEAllocation(prev => {
                            const nextAll = { ...prev } as typeof prev;
                            delete (nextAll as any)[taskGroup];
                            // Determine global mode based on whether any other group still has non-zero pillar data
                            const hasAnyOtherPillar = Object.entries(nextAll).some(([g, v]) => {
                              if (!v || !v.ftesByRegion) return false;
                              return REGIONS.some(r => (v.ftesByRegion[r] || 0) > 0);
                            });
                            // Defer persistence to avoid setState during render
                            safeUpdateSection('taskFTEAllocation', {
                              pillarFTEAllocation: nextAll,
                              allocationMode: hasAnyOtherPillar ? 'pillar' : 'activity',
                              totalFTEsPerRegion: { ...totalFTEsPerRegion },
                              fteCostsByRegion: { ...fteCostsByRegion },
                              regionalTotals: { ...regionalTotals },
                              fteMatrix: { ...fteMatrix },
                              customActivities: [...customActivities],
                              automationPercentages: { ...automationPercentages },
                              rampByPillarYear,
                              rationaleOverrides
                            });
                            return nextAll;
                          });
                          // 2) Switch the view mode for the group to Activity
                          setViewMode(prev => ({ ...prev, [taskGroup]: 'activity' }));
                          markUnsaved();
                        }}
                        className={`px-4 py-2 text-sm rounded-md transition-colors ${
                          (!viewMode[taskGroup] || viewMode[taskGroup] === 'activity')
                            ? 'bg-primary text-black font-medium'
                            : 'bg-[#334155] text-gray-300 hover:bg-[#334155]/70'
                        }`}
                      >
                        ● By Activity
                      </button>
                      <button
                        type="button"
                        data-cy={`allocation-mode-pillar-${taskGroup.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        onClick={() => {
                          lastModeChangeRef.current = Date.now();
                          preventHydrationTemporarily();

                          // 1) Clear By Activity data for this group's activities in both UI state and persisted payload
                          const clearedMatrix = buildClearedMatrixForGroup(taskGroup, fteMatrix);
                          setFTEMatrix(clearedMatrix);
                          // Also clear any in-progress input values for this group's activities
                          setInputValues(prev => {
                            const next = { ...prev } as Record<string, Record<string, string>>;
                            const activities = getActivitiesByGroup(taskGroup);
                            activities.forEach(a => { delete next[a.activityName]; });
                            return next;
                          });
                          // Also clear automation percentages for this group's activities so they don't linger
                          const groupActivities = getActivitiesByGroup(taskGroup);
                          const prunedAutomation = { ...automationPercentages } as Record<string, number>;
                          groupActivities.forEach(a => { delete (prunedAutomation as any)[a.activityName]; });
                          setAutomationPercentages(prunedAutomation);

                          // 2) Ensure scaffold exists for this pillar group and PREFILL from activity sums (before they were cleared)
                          setPillarFTEAllocation(prev => {
                            const exists = prev[taskGroup];
                            const currentByRegion = exists?.ftesByRegion || {};
                            // Determine if current pillar data is effectively empty (all zeros or undefined)
                            const isEmpty = REGIONS.every(r => (currentByRegion[r] || 0) === 0);
                            const baseFromActivities = pillarTotals[taskGroup] || {};
                            const ftesByRegion: Record<string, number> = {};
                            REGIONS.forEach(r => {
                              // Prefill from activity sums if empty; otherwise retain existing values
                              ftesByRegion[r] = isEmpty ? (baseFromActivities[r] || 0) : (currentByRegion[r] ?? 0);
                            });
                            const scaffold = exists ? { ...exists, ftesByRegion } : { ftesByRegion, automationPercentage: 0 };
                            const nextPillar = { ...prev, [taskGroup]: scaffold };

                            // Persist immediately with pillar mode and CLEARED activity matrix
                            safeUpdateSection('taskFTEAllocation', { 
                              pillarFTEAllocation: nextPillar, 
                              allocationMode: 'pillar',
                              totalFTEsPerRegion: { ...totalFTEsPerRegion },
                              fteCostsByRegion: { ...fteCostsByRegion },
                              regionalTotals: { ...regionalTotals },
                              fteMatrix: clearedMatrix,
                              customActivities: [...customActivities],
                              automationPercentages: prunedAutomation,
                              rampByPillarYear,
                              rationaleOverrides
                            });
                            return nextPillar;
                          });
                          setRampByPillarYear(prev => ({
                            ...prev,
                            [taskGroup]: prev[taskGroup] || [...DEFAULT_RAMP_VECTOR] as RampVector
                          }));

                          // 3) Switch the view mode for the group
                          setViewMode(prev => ({ ...prev, [taskGroup]: 'pillar' }));
                          markUnsaved();
                        }}
                        className={`px-4 py-2 text-sm rounded-md transition-colors ${
                          viewMode[taskGroup] === 'pillar'
                            ? 'bg-primary text-black font-medium'
                            : 'bg-[#334155] text-gray-300 hover:bg-[#334155]/70'
                        }`}
                      >
                        ○ By Pillar
                      </button>
                    </div>

                    {/* Conditional rendering based on per-group view mode */}
                    {(!viewMode[taskGroup] || viewMode[taskGroup] === 'activity') ? (
                      // Existing By Activity view
                      <div className="overflow-x-auto">
                        {/* Sticky Header Row */}
                        <div className="sticky top-0 bg-forestBlack z-10 border-b border-[#334155] shadow-sm">
                          <div className="grid grid-cols-12 gap-x-4 px-4 py-2 text-sm font-medium text-gray-300">
                            <div className="col-span-3 text-left">Activity</div>
                            <div className="col-span-1 text-center">Frequency</div>
                            <div className="col-span-6 grid grid-cols-5 text-center">
                              {REGIONS.map(region => (
                                <div key={region}>
                                  {/* ** Changes made to allocation screen 30 July ** */}
                                  {region.toUpperCase()}
                                  {/* ** End of changes made to allocation screen 30 July ** */}
                                </div>
                              ))}
                            </div>
                            <div className="col-span-2 text-center text-primary pr-4">Automation %</div>
                          </div>
                        </div>

                        {/* Activity Rows */}
                        <div className="space-y-2 mt-2">
                          {activities.map(activity => (
                            <div key={activity.activityName} className="grid grid-cols-12 gap-x-4 px-4 py-2 border-b border-[#334155]/50 items-center">
                              {/* Activity Name */}
                              <div className="col-span-3 text-white text-sm leading-normal tracking-normal flex items-center gap-2">
                                <span>{activity.activityName}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-gray-400 hover:text-white"
                                  onClick={() => openRationaleModal(activity.activityName)}
                                  data-cy={`activity-help-${activity.activityName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
                                >
                                  <Info className="w-4 h-4" />
                                </Button>
                              </div>

                              {/* Frequency Column */}
                              <div className="col-span-1 flex justify-center">
                                <Badge
                                  variant="outline"
                                  className={`text-xs ${
                                    activity.frequency === 'Daily' ? 'border-redwoodLightBlue text-redwoodLightBlue' : //lightblue
                                    activity.frequency === 'Weekly' ? 'border-primary text-primary' : // purple
                                    activity.frequency === 'Monthly' ? 'border-orange text-orange' : // orange 
                                    activity.frequency === 'Quarterly' ? 'border-tangerine text-tangerine' : //tangerina
                                    'border-purple text-purple' //grey
                                  }`}
                                >
                                  {activity.frequency}
                                </Badge>
                              </div>

                              {/* FTE Inputs */}
                              <div className="col-span-6 grid grid-cols-5 items-center">
                                {/* All FTE Inputs */}
                                {REGIONS.map(region => (
                                  <div key={region} className="flex justify-center">
                                    <Input
                                      type="number"
                                      value={
                                        inputValues[activity.activityName]?.[region] ??
                                        (fteMatrix[activity.activityName]?.[region] || '')
                                      }
                                      onChange={(e) => handleFTEInputChange(activity.activityName, region, e.target.value)}
                                      onBlur={(e) => handleFTEInputBlur(activity.activityName, region, e.target.value)}
                                      placeholder="0"
                                      className="w-16 h-10 text-right px-2 bg-redwoodInputFieldBg  border-[#334155] text-white text-sm"
                                      data-cy={`fte-input-${activity.activityName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}-${region.toLowerCase().replace(/\s+/g, '-')}`}
                                    />
                                  </div>
                                ))}
                              </div>

                              {/* Automation Percentage */}
                              <div className="col-span-2 flex items-center justify-center pr-4">
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={automationPercentages[activity.activityName] ?? ''}
                                    onChange={(e) => {
                                      const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                                      setAutomationPercentages(prev => ({
                                        ...prev,
                                        [activity.activityName]: value
                                      }))
                                      markUnsaved();
                                    }}
                                    placeholder="0"
                                    className="w-16 h-10 text-right px-2 bg-redwoodInputFieldBg border-primary text-primary placeholder-primary/50 text-sm"
                                    data-cy={`automation-input-${activity.activityName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
                                  />
                                  <span className="text-primary text-sm">%</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      // ─────────────── FTE Allocation Toggle: Start ───────────────
                      // Summary row UI when 'By Pillar' selected
                      <div className="space-y-4">
                        <p className="text-sm text-gray-400 italic">
                          Enter total FTEs by region and the automation % you believe is achievable with Redwood for this entire pillar.
                        </p>
                        <div className="overflow-x-auto">
                          {/* Header Row with Region Labels */}
                          <div className="grid grid-cols-12 gap-x-4 px-4 py-2 text-sm font-medium text-gray-300">
                            <div className="col-span-3 text-left">Activity</div>
                            <div className="col-span-1"></div>
                            <div className="col-span-6 grid grid-cols-5 text-center">
                              {REGIONS.map(region => (
                                <div key={region}>
                                  {region.toUpperCase()}
                                </div>
                              ))}
                            </div>
                            <div className="col-span-2 text-center text-primary pr-4">Automation %</div>
                          </div>

                          {/* Summary Input Row */}
                          <div className="grid grid-cols-12 gap-x-4 px-4 py-3 bg-[#334155]/20 rounded">
                            {/* Label */}
                            <div className="col-span-3 text-white font-medium text-sm flex items-center gap-2">
                              <span>Overall FTE Allocation for this Pillar</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-gray-400 hover:text-white"
                                onClick={() => setSelectedPillarForRampModal(taskGroup)}
                                title="Edit Year 1-5 ramp"
                              >
                                <Info className="w-4 h-4" />
                              </Button>
                            </div>

                            {/* Empty frequency column */}
                            <div className="col-span-1"></div>

                            {/* Regional inputs (editable) */}
                            <div className="col-span-6 grid grid-cols-5">
                              {REGIONS.map(region => (
                                <div key={region} className="flex justify-center">
                                  <Input
                                    type="number"
                                    value={pillarFTEAllocation[taskGroup]?.ftesByRegion?.[region] ?? ''}
                                    onChange={(e) => handlePillarFTEChange(taskGroup, region, e.target.value)}
                                    placeholder="0"
                                    className="w-16 h-10 text-right px-2 bg-redwoodInputFieldBg  border-[#334155] text-white text-sm"
                                    data-cy={`pillar-fte-${taskGroup.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${region.toLowerCase().replace(/\s+/g, '-')}`}
                                  />
                                </div>
                              ))}
                            </div>

                            {/* Automation percentage (editable) */}
                            <div className="col-span-2 flex items-center justify-center pr-4">
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={pillarFTEAllocation[taskGroup]?.automationPercentage ?? ''}
                                  onChange={(e) => handlePillarAutomationChange(taskGroup, e.target.value)}
                                  placeholder="0"
                                  className="w-16 h-10 text-right px-2 bg-redwoodInputFieldBg border-primary text-primary placeholder-primary/50 text-sm"
                                  data-cy={`pillar-automation-${taskGroup.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                />
                                <span className="text-primary text-sm">%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      // ─────────────── FTE Allocation Toggle: End ───────────────
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          )
        })}

        {/* Custom Activities Section */}
        {customActivities.length > 0 && (
          <Card className="bg-forestBlack border-[#334155]">
            <Collapsible
              open={!collapsedSections.other}
              onOpenChange={(open) => setCollapsedSections(prev => ({ ...prev, other: !open }))}
            >
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer">
                  <CardTitle className="flex items-center gap-3 text-white">
                    {collapsedSections.other ? (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                    <Users className="w-5 h-5 text-primary" />
                    Other Activities
                    <Badge variant="secondary" className="ml-2">
                      {customActivities.length} custom
                    </Badge>
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <CardContent className="px-4 py-2">
              <div className="overflow-x-auto">
                {/* Header Row */}
                <div className="sticky top-0 bg-forestBlack z-10 border-b border-[#334155] shadow-sm">
                  <div className="grid grid-cols-12 gap-x-4 px-4 py-2 text-sm font-medium text-gray-300">
                    <div className="col-span-3 text-left">Activity</div>
                    <div className="col-span-1 text-center">Frequency</div>
                    <div className="col-span-6 grid grid-cols-5 text-center">
                      {REGIONS.map(region => (
                        <div key={region}>
                          {region}
                        </div>
                      ))}
                    </div>
                    <div className="col-span-2 text-center text-primary pr-4">Automation %</div>
                  </div>
                </div>
                
                {/* Custom Activity Rows */}
                <div className="space-y-2 mt-2">
                  {customActivities.map(activity => (
                    <div
                      key={activity.id}
                      className="grid grid-cols-12 gap-x-4 px-4 py-2 border-b border-[#334155]/50 items-center"
                      data-testid={`custom-activity-${activity.id}`}
                      data-cy={`custom-activity-${activity.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {/* Activity Name with Delete Button */}
                      <div className="col-span-3 flex items-center gap-2">
                        <Input
                          value={activity.name}
                          onChange={(e) => updateCustomActivityName(activity.id, e.target.value)}
                          className="bg-redwoodInputFieldBg  border-[#334155] text-white text-sm h-10 flex-1"
                          placeholder="Activity name"
                          data-testid={`custom-activity-name-${activity.id}`}
                          data-cy={`custom-activity-name-input`}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCustomActivity(activity.id)}
                          className="h-10 w-10 p-0 text-gray-400 hover:text-red-400"
                          data-testid={`custom-activity-remove-${activity.id}`}
                          data-cy="custom-activity-remove"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-10 w-10 p-0 text-gray-400 hover:text-white"
                          onClick={() => openRationaleModal(activity.name)}
                        >
                          <Info className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Frequency Column */}
                      <div className="col-span-1 flex justify-center">
                        <Select
                          value={activity.frequency}
                          onValueChange={(value: 'Daily' | 'Monthly') => updateCustomActivityFrequency(activity.id, value)}
                        >
                          <SelectTrigger className="w-20 h-10 bg-redwoodInputFieldBg  border-[#334155] text-white text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Daily">Daily</SelectItem>
                            <SelectItem value="Monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* FTE Inputs */}
                      <div className="col-span-6 grid grid-cols-5 items-center">
                        {/* All FTE Inputs */}
                        {REGIONS.map(region => (
                          <div key={region} className="flex justify-center">
                            <Input
                              type="number"
                              data-cy={`fte-input-${activity.name.toLowerCase().replace(/\s+/g, '-')}-${region.toLowerCase().replace(/\s+/g, '-')}`}
                              value={
                                inputValues[activity.name]?.[region] ??
                                (fteMatrix[activity.name]?.[region] || '')
                              }
                              onChange={(e) => handleFTEInputChange(activity.name, region, e.target.value)}
                              onBlur={(e) => handleFTEInputBlur(activity.name, region, e.target.value)}
                              placeholder="0"
                              className="w-16 h-10 text-right px-2 bg-redwoodInputFieldBg  border-[#334155] text-white text-sm"
                            />
                          </div>
                        ))}
                      </div>
                      
                      {/* Automation Percentage */}
                      <div className="col-span-2 flex items-center justify-center pr-4">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            data-cy={`automation-input-${activity.name.toLowerCase().replace(/\s+/g, '-')}`}
                            min="0"
                            max="100"
                            value={automationPercentages[activity.name] ?? ''}
                            onChange={(e) => {
                              const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                              setAutomationPercentages(prev => ({
                                ...prev,
                                [activity.name]: value
                              }))
                              markUnsaved();
                            }}
                            placeholder="0"
                            className="w-16 h-10 text-right px-2 bg-redwoodInputFieldBg border-primary text-primary placeholder-primary/50 text-sm"
                          />
                          <span className="text-primary text-sm">%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        )}

        {/* Add Custom Activity Button */}
        <Card className="bg-redwoodInputFieldBg  border-[#334155] border-dashed">
          <CardContent className="pt-6">
            <Button
              variant="outline"
              onClick={addCustomActivity}
              className="w-full bg-transparent border-[#334155] text-gray-300 hover:bg-[#334155]"
              data-testid="add-custom-activity"
              data-cy="add-custom-activity"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Custom Activity
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Validation Summary Card */}
      <Card className="bg-forestBlack border-[#334155] mt-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">FTE Allocation Status</h3>
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-300">
                  <span className="font-medium">Total Allocated:</span> {Object.values(allocatedTotalsByRegion).reduce((sum, total) => sum + total, 0).toFixed(2)} FTEs
                </div>
                <div className="text-sm text-gray-300">
                  <span className="font-medium">Total Available:</span> {Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0).toFixed(2)} FTEs
                </div>
                <div className="text-sm text-gray-300">
                  <span className="font-medium">Allocation:</span> 
                  <span className={`ml-1 font-bold ${
                    (Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0) > 0 ? 
                      Math.round((Object.values(allocatedTotalsByRegion).reduce((sum, total) => sum + total, 0) / Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0)) * 100) : 0) >= 90
                      ? 'text-primary' 
                      : 'text-[#FF715A]'
                  }`}>
                    {Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0) > 0 ? 
                      Math.round((Object.values(allocatedTotalsByRegion).reduce((sum, total) => sum + total, 0) / Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0)) * 100) : 0}%
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {(Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0) > 0 ? 
                Math.round((Object.values(allocatedTotalsByRegion).reduce((sum, total) => sum + total, 0) / Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0)) * 100) : 0) >= 90 ? (
                <>
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Valid (≥90%)</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-[#FF715A]" />
                  <span className="text-[#FF715A] font-medium">Under-allocated</span>
                </>
              )}
            </div>
          </div>
          
          {/* Warning message for under-allocation */}
          {(Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0) > 0 ? 
            Math.round((Object.values(allocatedTotalsByRegion).reduce((sum, total) => sum + total, 0) / Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0)) * 100) : 0) < 90 && (
            <div className="mt-4 p-3 bg-[#FF715A] border border-[#FF715A] rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-forestBlack mt-0.5 flex-shrink-0" />
                <div className="text-sm text-forestBlack">
                  <strong>Minimum Allocation Required:</strong> You must allocate at least 90% of available FTEs to proceed. 
                  Current allocation: {Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0) > 0 ? 
                    Math.round((Object.values(allocatedTotalsByRegion).reduce((sum, total) => sum + total, 0) / Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0)) * 100) : 0}%
                  {(Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0) > 0 ? 
                    Math.round((Object.values(allocatedTotalsByRegion).reduce((sum, total) => sum + total, 0) / Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0)) * 100) : 0) < 90 && 
                    ` — please increase by ${90 - (Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0) > 0 ? 
                      Math.round((Object.values(allocatedTotalsByRegion).reduce((sum, total) => sum + total, 0) / Object.values(totalFTEsPerRegion).reduce((sum, total) => sum + total, 0)) * 100) : 0)}%`}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button 
          variant="outline" 
          onClick={handleBack}
          className="bg-transparent border-[#334155] text-gray-300 hover:bg-[#334155]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <Button 
          onClick={handleContinue}
          disabled={!isValid}
          className={`${
            isValid
              ? "bg-primary hover:bg-primaryHover text-forestBlack"
              : "bg-gray-600 cursor-not-allowed text-gray-400"
          }`}
          data-cy="completeAloocation"
        >
          Continue
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      <Dialog open={!!selectedActivityForHelp} onOpenChange={(open) => !open && setSelectedActivityForHelp(null)}>
        <DialogContent className="sm:max-w-xl bg-forestBlack border-[#334155]">
          <DialogHeader>
            <DialogTitle className="text-white">Activity Rationale</DialogTitle>
            <DialogDescription className="sr-only">
              Review the default rationale and optionally save an assessment-specific override for this activity.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-300">{selectedActivityForHelp}</p>
            <div className="space-y-2">
              <Label className="text-gray-300">Override for this assessment</Label>
              <Textarea
                value={rationaleDraft}
                onChange={(e) => setRationaleDraft(e.target.value)}
                className="min-h-[140px] bg-redwoodInputFieldBg border-[#334155] text-white"
              />
            </div>
            <div className="rounded border border-[#334155] bg-[#0F172A] p-3 text-xs text-gray-300 whitespace-pre-wrap">
              <span className="font-semibold text-gray-200">Default rationale:</span>{" "}
              {selectedActivityForHelp ? getActivityHelpText(selectedActivityForHelp) : ""}
            </div>
            <div className="flex justify-between">
              <Button type="button" variant="outline" className="border-[#334155] text-gray-300" onClick={resetRationaleToDefault}>
                Reset to Default
              </Button>
              <Button type="button" className="bg-primary hover:bg-primaryHover text-forestBlack" onClick={saveRationaleOverride}>
                Save Override
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedPillarForRampModal} onOpenChange={(open) => !open && setSelectedPillarForRampModal(null)}>
        <DialogContent className="sm:max-w-2xl bg-forestBlack border-[#334155]">
          <DialogHeader>
            <DialogTitle className="text-white">
              {selectedPillarForRampModal ? `${selectedPillarForRampModal} Ramp (Years 1-5)` : "Ramp"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Adjust yearly ramp percentages from Year 1 to Year 5 for the selected pillar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-5 gap-3">
            {[0, 1, 2, 3, 4].map((yearIndex) => (
              <div key={yearIndex} className="space-y-2">
                <Label className="text-gray-300">{`Year ${yearIndex + 1}`}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={
                      selectedPillarForRampModal
                        ? (rampByPillarYear[selectedPillarForRampModal] || DEFAULT_RAMP_VECTOR)[yearIndex]
                        : 100
                    }
                    onChange={(e) => {
                      if (!selectedPillarForRampModal) return;
                      handleRampChange(selectedPillarForRampModal, yearIndex, e.target.value);
                    }}
                    className="bg-redwoodInputFieldBg border-[#334155] text-white"
                  />
                  <span className="text-gray-300">%</span>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>


    </div>
  )
}
